import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { apiFetch, useAuth, useToast } from '../App';
import { ga } from '../utils/ga4';
import { ArrowLeft, ArrowRight, Check, Calendar, Clock, User, Scissors, CreditCard, Camera, Loader2, Plus, X, Package } from 'lucide-react';

function loadRazorpayScript() {
  return new Promise(resolve => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function formatPrice(p) {
  return p === 0 ? 'Varies' : `₹${p.toLocaleString('en-IN')}`;
}

export default function BookingFlow() {
  const { nodeSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, setShowAuth } = useAuth();
  const addToast = useToast();

  // Steps: 1=Pet, 2=Package, 3=AddOns, 4=Schedule, 5=Review
  const [step, setStep] = useState(1);
  const [node, setNode] = useState(null);
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Booking state
  const [selectedPet, setSelectedPet] = useState(null);
  const [selectedCatalogId, setSelectedCatalogId] = useState(searchParams.get('catalog') || null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedAlaCarteIds, setSelectedAlaCarteIds] = useState({});
  // Legacy
  const [selectedService, setSelectedService] = useState(searchParams.get('service') || null);

  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedGroomer, setSelectedGroomer] = useState(null);
  const [liveStream, setLiveStream] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [useCredits, setUseCredits] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('ONLINE');
  const [notes, setNotes] = useState('');
  const [slots, setSlots] = useState([]);
  const [newPetName, setNewPetName] = useState('');
  const [newPetSpecies, setNewPetSpecies] = useState('DOG');
  const [newPetBreed, setNewPetBreed] = useState('');
  const [newPetSize, setNewPetSize] = useState('MEDIUM');
  const [showNewPet, setShowNewPet] = useState(false);

  useEffect(() => {
    if (!user) { setShowAuth(true); return; }
    Promise.all([
      apiFetch(`/api/nodes/${nodeSlug}`),
      apiFetch('/api/pets'),
    ]).then(([n, p]) => {
      setNode(n);
      setPets(p);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [nodeSlug, user]);

  useEffect(() => {
    if (selectedGroomer && selectedDate) {
      apiFetch(`/api/groomers/${selectedGroomer}/slots?date=${selectedDate}`)
        .then(d => setSlots(d.slots || []))
        .catch(() => setSlots([]));
    }
  }, [selectedGroomer, selectedDate]);

  // Determine if this store uses catalog or legacy services
  const catalog = node?.catalog || [];
  const hasCatalog = catalog.length > 0;
  const pet = pets.find(p => p.id === selectedPet);

  // Available sizes for selected pet (RJY has EXTRA_LARGE)
  const availableSizes = useMemo(() => {
    if (!node || !hasCatalog) return ['SMALL', 'MEDIUM', 'LARGE'];
    const allSizes = new Set();
    catalog.filter(c => c.type === 'PACKAGE').forEach(p => {
      p.variants.forEach(v => { if (v.size !== 'ALL') allSizes.add(v.size); });
    });
    return allSizes.size > 0 ? [...allSizes] : ['SMALL', 'MEDIUM', 'LARGE'];
  }, [node, catalog, hasCatalog]);

  // Filter packages by pet species
  const packages = useMemo(() => {
    if (!pet || !hasCatalog) return catalog.filter(c => c.type === 'PACKAGE');
    return catalog.filter(c => c.type === 'PACKAGE' && c.species === pet.species);
  }, [catalog, pet, hasCatalog]);

  // Selected package
  const selectedPkg = catalog.find(c => c.id === selectedCatalogId);

  // Get package price for selected size
  const pkgPrice = useMemo(() => {
    if (!selectedPkg) return 0;
    const v = selectedPkg.variants.find(v => v.size === selectedSize || v.size === 'ALL');
    return v ? v.price : selectedPkg.variants[0]?.price || 0;
  }, [selectedPkg, selectedSize]);

  // Determine included services in the selected package (for filtering a-la-carte)
  const includedServiceNames = useMemo(() => {
    if (!selectedPkg) return new Set();
    const names = new Set();
    const desc = (selectedPkg.description || '').toLowerCase();
    // Split by pipe and normalize
    desc.split('|').forEach(s => {
      const trimmed = s.trim();
      if (trimmed) names.add(trimmed);
    });
    return names;
  }, [selectedPkg]);

  // Available a-la-carte items (filter out items already included in package)
  const alaCarteItems = useMemo(() => {
    if (!pet || !hasCatalog) return [];
    const items = catalog.filter(c => c.type === 'ALA_CARTE' && c.species === pet.species);
    return items.filter(item => {
      const itemName = item.name.toLowerCase().trim();
      // Check if any included service name contains or is contained in this item name
      for (const inc of includedServiceNames) {
        if (inc.includes(itemName) || itemName.includes(inc)) return false;
      }
      return true;
    });
  }, [catalog, pet, hasCatalog, includedServiceNames]);

  // A-la-carte total
  const alaCarteTotal = useMemo(() => {
    let total = 0;
    for (const [id, selected] of Object.entries(selectedAlaCarteIds)) {
      if (!selected) continue;
      const item = catalog.find(c => c.id === id);
      if (item) {
        const v = item.variants.find(v => v.size === selectedSize || v.size === 'ALL');
        total += v ? v.price : item.variants[0]?.price || 0;
      }
    }
    return total;
  }, [selectedAlaCarteIds, catalog, selectedSize]);

  // Get selected a-la-carte details for booking
  const getSelectedAlaCarteDetails = () => {
    const details = [];
    for (const [id, selected] of Object.entries(selectedAlaCarteIds)) {
      if (!selected) continue;
      const item = catalog.find(c => c.id === id);
      if (item) {
        const v = item.variants.find(v => v.size === selectedSize || v.size === 'ALL');
        details.push({ id: item.id, name: item.name, price: v ? v.price : item.variants[0]?.price || 0 });
      }
    }
    return details;
  };

  // Legacy service stuff
  const service = node?.services?.find(s => s.id === selectedService);
  const legacyPrice = service?.effectivePrice || service?.basePrice || 0;

  // Base price (package or legacy)
  const basePrice = hasCatalog ? pkgPrice : legacyPrice;
  const streamFee = liveStream ? 149 : 0;
  const total = basePrice + (hasCatalog ? alaCarteTotal : 0) + streamFee;
  const creditsAvailable = user?.creditBalance || 0;
  const maxCredits = total * 0.2;
  const creditsToUse = useCredits ? Math.min(creditsAvailable, maxCredits) : 0;
  const netAmount = total - couponDiscount - creditsToUse;

  const groomer = node?.groomers?.find(g => g.id === selectedGroomer);

  // Handlers
  const handleAddPet = async () => {
    if (!newPetName) return;
    try {
      const newPet = await apiFetch('/api/pets', {
        method: 'POST',
        body: JSON.stringify({ name: newPetName, species: newPetSpecies, breed: newPetBreed, size: newPetSize }),
      });
      setPets([...pets, newPet]);
      setSelectedPet(newPet.id);
      setShowNewPet(false);
      addToast(`${newPetName} added!`);
    } catch (e) { addToast(e.message, 'error'); }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode) return;
    try {
      const res = await apiFetch('/api/coupons/validate', {
        method: 'POST',
        body: JSON.stringify({ code: couponCode, amount: total, nodeId: node.id }),
      });
      if (res.valid) { setCouponDiscount(res.discount); addToast(`Coupon applied! You save ₹${res.discount}`); }
      else { addToast(res.message, 'error'); setCouponDiscount(0); }
    } catch { addToast('Invalid coupon', 'error'); }
  };

  const toggleAlaCarte = (id) => {
    setSelectedAlaCarteIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const body = {
        petId: selectedPet,
        nodeId: node.id,
        mode: node.type,
        groomerId: selectedGroomer || undefined,
        scheduledStart: selectedDate && selectedTime ? `${selectedDate}T${selectedTime}:00Z` : undefined,
        paymentMethod,
        liveStreamEnabled: liveStream,
        couponCode: couponDiscount > 0 ? couponCode : undefined,
        creditsToUse,
        notes,
        sourceChannel: 'WEB',
      };

      if (hasCatalog) {
        body.catalogItemId = selectedCatalogId;
        body.selectedSize = selectedSize;
        body.alaCarteItems = getSelectedAlaCarteDetails();
      } else {
        body.serviceId = selectedService;
      }

      const booking = await apiFetch('/api/bookings', { method: 'POST', body: JSON.stringify(body) });

      // GA4: track booking confirmed (server has accepted the booking)
      ga.bookingConfirmed(booking.id, netAmount, body.serviceId || body.catalogItemId);

      if (paymentMethod === 'CASH') {
        addToast('Booking confirmed! Pay at the store.');
        navigate('/paws/bookings');
        setSubmitting(false);
        return;
      }

      const loaded = await loadRazorpayScript();
      if (!loaded) { addToast('Could not load payment gateway.', 'error'); setSubmitting(false); return; }

      const order = await apiFetch('/api/payments/create-order', {
        method: 'POST',
        body: JSON.stringify({ bookingId: booking.id, amount: Math.round(netAmount) }),
      });

      const svcName = hasCatalog ? selectedPkg?.name : service?.name;
      const options = {
        key: order.keyId, amount: order.amount, currency: order.currency, order_id: order.orderId,
        name: 'Petfolk', description: `${svcName} at ${node?.displayName}`, image: '/logo192.png',
        prefill: { name: user.name || '', contact: user.phone || '', email: user.email || '' },
        theme: { color: '#FF8C42' },
        handler: async (response) => {
          try {
            await apiFetch('/api/payments/verify', {
              method: 'POST',
              body: JSON.stringify({
                bookingId: booking.id, razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id, razorpay_signature: response.razorpay_signature,
              }),
            });
            addToast('Payment successful! Booking confirmed.');
            navigate('/paws/bookings');
          } catch { addToast('Payment verification failed.', 'error'); }
        },
        modal: {
          ondismiss: () => { addToast('Payment cancelled. Your booking is saved.', 'warning'); navigate('/paws/bookings'); },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => addToast('Payment failed.', 'error'));
      rzp.open();
    } catch (e) { addToast(e.message, 'error'); }
    setSubmitting(false);
  };

  // Date picker
  const dates = [];
  for (let i = 0; i < 14; i++) {
    const d = new Date(); d.setDate(d.getDate() + i);
    dates.push(d.toISOString().split('T')[0]);
  }

  // Steps definition
  const stepDefs = hasCatalog
    ? [
        { num: 1, label: 'Pet', icon: User },
        { num: 2, label: 'Package', icon: Package },
        { num: 3, label: 'Add-ons', icon: Plus },
        { num: 4, label: 'Schedule', icon: Calendar },
        { num: 5, label: 'Review', icon: CreditCard },
      ]
    : [
        { num: 1, label: 'Pet', icon: User },
        { num: 2, label: 'Service', icon: Scissors },
        { num: 3, label: 'Schedule', icon: Calendar },
        { num: 4, label: 'Review', icon: CreditCard },
      ];

  const totalSteps = stepDefs.length;
  const scheduleStep = hasCatalog ? 4 : 3;
  const reviewStep = hasCatalog ? 5 : 4;

  if (!user) return (
    <div className="min-h-screen flex items-center justify-center pt-20" style={{ background: 'var(--bg-primary)' }}>
      <div className="text-center">
        <h2 className="font-outfit text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Please sign in to book</h2>
        <button onClick={() => setShowAuth(true)} className="btn-primary" data-testid="sign-in-btn">Sign In</button>
      </div>
    </div>
  );

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center pt-20" style={{ background: 'var(--bg-primary)' }}>
      <div className="w-12 h-12 border-4 border-petfolk-orange border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen pt-20 pb-12" style={{ background: 'var(--bg-primary)' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-black/5 dark:hover:bg-white/5" data-testid="booking-back-btn">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Book at {node?.displayName}</h1>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{node?.address}</p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-10">
          {stepDefs.map((s, i) => (
            <React.Fragment key={s.num}>
              <button
                onClick={() => s.num < step && setStep(s.num)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                  step === s.num ? 'bg-petfolk-orange text-white' : step > s.num ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : ''
                }`}
                style={{ color: step < s.num ? 'var(--text-secondary)' : undefined }}
                data-testid={`step-${s.num}`}
              >
                {step > s.num ? <Check size={14} /> : <s.icon size={14} />}
                <span className="hidden sm:inline">{s.label}</span>
              </button>
              {i < stepDefs.length - 1 && <div className="flex-1 h-0.5 mx-1.5" style={{ background: step > s.num ? '#22C55E' : 'var(--border-color)' }} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Pet Selection */}
        {step === 1 && (
          <div className="animate-fade-in" data-testid="step-pet-selection">
            <h2 className="font-outfit text-xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>Select Your Pet</h2>
            <div className="grid gap-4">
              {pets.map(p => (
                <button key={p.id} data-testid={`pet-option-${p.id}`}
                  onClick={() => { setSelectedPet(p.id); setSelectedCatalogId(null); setSelectedSize(null); setSelectedAlaCarteIds({}); }}
                  className={`petfolk-card p-5 flex items-center gap-4 text-left ${selectedPet === p.id ? 'ring-2 ring-petfolk-orange' : ''}`}>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-petfolk-orange/20 to-petfolk-purple/10 flex items-center justify-center text-2xl shrink-0">
                    {p.species === 'DOG' ? '🐕' : '🐈'}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>{p.name}</h3>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{p.breed || p.species} {p.size && `· ${p.size}`}</p>
                  </div>
                  {selectedPet === p.id && <Check size={20} className="text-petfolk-orange" />}
                </button>
              ))}
              {!showNewPet ? (
                <button onClick={() => setShowNewPet(true)} className="petfolk-card p-5 border-dashed text-center" style={{ color: 'var(--text-secondary)' }} data-testid="add-pet-btn">
                  + Add New Pet
                </button>
              ) : (
                <div className="petfolk-card p-5 space-y-3" data-testid="new-pet-form">
                  <input className="petfolk-input" placeholder="Pet name" value={newPetName} onChange={e => setNewPetName(e.target.value)} data-testid="new-pet-name" />
                  <div className="flex gap-2">
                    {['DOG', 'CAT'].map(s => (
                      <button key={s} onClick={() => setNewPetSpecies(s)} className={`flex-1 py-2 rounded-xl text-sm font-semibold ${newPetSpecies === s ? 'bg-petfolk-orange text-white' : ''}`} style={{ color: newPetSpecies !== s ? 'var(--text-secondary)' : undefined, border: `1px solid var(--border-color)` }}>
                        {s === 'DOG' ? '🐕 Dog' : '🐈 Cat'}
                      </button>
                    ))}
                  </div>
                  <input className="petfolk-input" placeholder="Breed (optional)" value={newPetBreed} onChange={e => setNewPetBreed(e.target.value)} />
                  <div className="flex gap-2">
                    {availableSizes.map(sz => (
                      <button key={sz} onClick={() => setNewPetSize(sz)} className={`flex-1 py-2 rounded-xl text-xs font-semibold ${newPetSize === sz ? 'bg-petfolk-purple text-white' : ''}`} style={{ color: newPetSize !== sz ? 'var(--text-secondary)' : undefined, border: `1px solid var(--border-color)` }}>
                        {sz === 'EXTRA_LARGE' ? 'XL' : sz}
                      </button>
                    ))}
                  </div>
                  <button onClick={handleAddPet} className="btn-primary w-full justify-center" data-testid="save-new-pet">Save Pet</button>
                </div>
              )}
            </div>
            <button onClick={() => selectedPet && setStep(2)} disabled={!selectedPet} className="btn-primary mt-6 ml-auto" data-testid="next-to-service">
              Next <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* Step 2: Package/Service Selection */}
        {step === 2 && hasCatalog && (
          <div className="animate-fade-in" data-testid="step-package-selection">
            <h2 className="font-outfit text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Select Package</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
              Choose a grooming package for {pet?.name} ({pet?.species === 'DOG' ? 'Dog' : 'Cat'})
            </p>

            {/* Size selector */}
            {packages.some(p => p.variants.some(v => v.size !== 'ALL')) && (
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Pet Size</label>
                <div className="flex gap-2">
                  {availableSizes.map(sz => (
                    <button key={sz} onClick={() => setSelectedSize(sz)}
                      data-testid={`size-${sz}`}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${selectedSize === sz ? 'bg-petfolk-orange text-white' : ''}`}
                      style={{ border: `1px solid ${selectedSize === sz ? 'transparent' : 'var(--border-color)'}`, color: selectedSize !== sz ? 'var(--text-primary)' : undefined }}>
                      {sz === 'EXTRA_LARGE' ? 'Extra Large' : sz.charAt(0) + sz.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid gap-4">
              {packages.map(pkg => {
                const v = pkg.variants.find(v => v.size === selectedSize || v.size === 'ALL');
                const price = v ? v.price : pkg.variants[0]?.price || 0;
                const isSelected = selectedCatalogId === pkg.id;
                const included = pkg.description ? pkg.description.split(' | ') : [];
                const hasRequiredSize = pkg.variants.some(v => v.size === selectedSize || v.size === 'ALL');

                if (selectedSize && !hasRequiredSize) return null;

                return (
                  <button key={pkg.id} data-testid={`pkg-option-${pkg.id}`}
                    onClick={() => setSelectedCatalogId(pkg.id)}
                    className={`petfolk-card p-5 text-left transition-all ${isSelected ? 'ring-2 ring-petfolk-orange' : ''}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-outfit font-bold text-lg" style={{ color: 'var(--text-primary)' }}>{pkg.name}</h3>
                        {pkg.notes && <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: 'rgba(255,140,66,0.12)', color: '#c2410c' }}>{pkg.notes}</span>}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-outfit text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{formatPrice(price)}</span>
                        {isSelected && <Check size={20} className="text-petfolk-orange" />}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {included.slice(0, 5).map((item, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(34,197,94,0.08)', color: '#15803d' }}>
                          {item.trim()}
                        </span>
                      ))}
                      {included.length > 5 && (
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--border-color)', color: 'var(--text-secondary)' }}>
                          +{included.length - 5} more
                        </span>
                      )}
                    </div>
                    {v?.duration && (
                      <span className="text-xs mt-2 inline-block" style={{ color: 'var(--text-secondary)' }}>
                        <Clock size={12} className="inline mr-1" />{v.duration} min
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(1)} className="btn-outline" data-testid="back-to-pet">Back</button>
              <button onClick={() => selectedCatalogId && (selectedSize || selectedPkg?.variants[0]?.size === 'ALL') && setStep(3)}
                disabled={!selectedCatalogId || (!selectedSize && selectedPkg?.variants[0]?.size !== 'ALL')}
                className="btn-primary ml-auto" data-testid="next-to-addons">
                Next <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2 Legacy: Service Selection */}
        {step === 2 && !hasCatalog && (
          <div className="animate-fade-in" data-testid="step-service-selection">
            <h2 className="font-outfit text-xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>Select Service</h2>
            <div className="grid gap-4">
              {(node?.services || []).filter(s => !pet || s.species === pet.species || (!s.sizeCategory)).map(svc => (
                <button key={svc.id} data-testid={`svc-option-${svc.id}`}
                  onClick={() => setSelectedService(svc.id)}
                  className={`petfolk-card p-5 flex items-center gap-4 text-left ${selectedService === svc.id ? 'ring-2 ring-petfolk-orange' : ''}`}>
                  <div className="flex-1">
                    <h3 className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>{svc.name}</h3>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{svc.description}</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{svc.baseDurationMinutes} min</p>
                  </div>
                  <span className="font-outfit text-xl font-bold" style={{ color: 'var(--text-primary)' }}>₹{(svc.effectivePrice || svc.basePrice).toLocaleString()}</span>
                </button>
              ))}
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(1)} className="btn-outline" data-testid="back-to-pet">Back</button>
              <button onClick={() => selectedService && setStep(3)} disabled={!selectedService} className="btn-primary ml-auto" data-testid="next-to-schedule">
                Next <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: A-la-carte Add-ons (catalog only) */}
        {step === 3 && hasCatalog && (
          <div className="animate-fade-in" data-testid="step-addons">
            <h2 className="font-outfit text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Add Extra Services</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
              Optional add-ons to complement your {selectedPkg?.name} package. Items already included in your package are hidden.
            </p>

            {alaCarteItems.length > 0 ? (
              <div className="grid gap-3">
                {alaCarteItems.map(item => {
                  const v = item.variants.find(v => v.size === selectedSize || v.size === 'ALL');
                  const price = v ? v.price : item.variants[0]?.price || 0;
                  const isSelected = selectedAlaCarteIds[item.id];
                  return (
                    <button key={item.id} data-testid={`addon-${item.id}`}
                      onClick={() => toggleAlaCarte(item.id)}
                      className={`petfolk-card p-4 flex items-center gap-3 text-left transition-all ${isSelected ? 'ring-2 ring-petfolk-purple' : ''}`}>
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-petfolk-purple text-white' : ''}`}
                        style={{ background: !isSelected ? 'var(--bg-secondary)' : undefined, border: !isSelected ? '1.5px solid var(--border-color)' : undefined }}>
                        {isSelected ? <Check size={16} /> : <Plus size={16} style={{ color: 'var(--text-secondary)' }} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{item.name}</p>
                        {item.notes && <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{item.notes}</p>}
                      </div>
                      <span className="font-outfit font-bold text-sm shrink-0" style={{ color: isSelected ? '#7c3aed' : 'var(--text-primary)' }}>
                        {price === 0 ? 'Varies' : `+₹${price.toLocaleString('en-IN')}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="petfolk-card p-8 text-center">
                <p style={{ color: 'var(--text-secondary)' }}>All add-on services are already included in your selected package.</p>
              </div>
            )}

            {/* Running total */}
            {alaCarteTotal > 0 && (
              <div className="petfolk-card p-4 mt-4 flex items-center justify-between" style={{ background: 'rgba(139,92,246,0.06)' }}>
                <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Add-ons total</span>
                <span className="font-outfit font-bold" style={{ color: '#7c3aed' }}>+₹{alaCarteTotal.toLocaleString('en-IN')}</span>
              </div>
            )}

            {/* Live Stream Add-on */}
            <div className="petfolk-card p-4 mt-4 flex items-center gap-4" data-testid="livestream-addon">
              <Camera size={22} className="text-petfolk-purple" />
              <div className="flex-1">
                <h3 className="font-outfit font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Live Streaming</h3>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Watch grooming live! +₹149</p>
              </div>
              <button onClick={() => setLiveStream(!liveStream)} data-testid="livestream-toggle"
                className={`w-12 h-7 rounded-full relative transition-colors ${liveStream ? 'bg-petfolk-purple' : ''}`}
                style={{ background: !liveStream ? 'var(--border-color)' : undefined }}>
                <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${liveStream ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(2)} className="btn-outline" data-testid="back-to-package">Back</button>
              <button onClick={() => setStep(4)} className="btn-primary ml-auto" data-testid="next-to-schedule">
                Next <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Schedule Step (Step 3 legacy, Step 4 catalog) */}
        {step === scheduleStep && (
          <div className="animate-fade-in" data-testid="step-schedule">
            <h2 className="font-outfit text-xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>Choose Date & Time</h2>
            {node?.groomers?.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Preferred Groomer (optional)</label>
                <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
                  {node.groomers.map(g => (
                    <button key={g.id} onClick={() => setSelectedGroomer(g.id === selectedGroomer ? null : g.id)}
                      className={`petfolk-card px-4 py-3 flex items-center gap-3 shrink-0 ${selectedGroomer === g.id ? 'ring-2 ring-petfolk-orange' : ''}`}
                      data-testid={`groomer-${g.id}`}>
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-petfolk-orange to-petfolk-purple flex items-center justify-center text-white font-bold text-sm">
                        {g.name?.[0]}
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{g.name}</p>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{g.specialties?.slice(0, 2).join(', ')}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Select Date</label>
              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
                {dates.map(d => {
                  const dt = new Date(d);
                  return (
                    <button key={d} onClick={() => setSelectedDate(d)} data-testid={`date-${d}`}
                      className={`flex flex-col items-center px-4 py-3 rounded-2xl shrink-0 transition-all ${selectedDate === d ? 'bg-petfolk-orange text-white' : ''}`}
                      style={{ border: `1px solid ${selectedDate === d ? 'transparent' : 'var(--border-color)'}`, color: selectedDate !== d ? 'var(--text-primary)' : undefined }}>
                      <span className="text-xs font-medium opacity-70">{dt.toLocaleDateString('en', { weekday: 'short' })}</span>
                      <span className="text-lg font-bold">{dt.getDate()}</span>
                      <span className="text-xs opacity-70">{dt.toLocaleDateString('en', { month: 'short' })}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Select Time</label>
              {slots.length > 0 ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {slots.map(s => (
                    <button key={s.start} onClick={() => s.available && setSelectedTime(s.start)} disabled={!s.available}
                      data-testid={`time-${s.start}`}
                      className={`py-3 rounded-xl text-sm font-semibold transition-all ${selectedTime === s.start ? 'bg-petfolk-orange text-white' : s.available ? '' : 'opacity-30 cursor-not-allowed'}`}
                      style={{ border: `1px solid ${selectedTime === s.start ? 'transparent' : 'var(--border-color)'}`, color: selectedTime !== s.start && s.available ? 'var(--text-primary)' : undefined }}>
                      {s.start}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {['09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00'].map(t => (
                    <button key={t} onClick={() => setSelectedTime(t)} data-testid={`time-${t}`}
                      className={`py-3 rounded-xl text-sm font-semibold transition-all ${selectedTime === t ? 'bg-petfolk-orange text-white' : ''}`}
                      style={{ border: `1px solid ${selectedTime === t ? 'transparent' : 'var(--border-color)'}`, color: selectedTime !== t ? 'var(--text-primary)' : undefined }}>
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <textarea className="petfolk-input mb-4" placeholder="Any special notes for the groomer?" rows={2} value={notes} onChange={e => setNotes(e.target.value)} data-testid="booking-notes" />

            {/* Live stream toggle for legacy flow */}
            {!hasCatalog && (
              <div className="petfolk-card p-4 mb-4 flex items-center gap-4" data-testid="livestream-addon">
                <Camera size={22} className="text-petfolk-purple" />
                <div className="flex-1">
                  <h3 className="font-outfit font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Add Live Streaming</h3>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Watch grooming live! +₹149</p>
                </div>
                <button onClick={() => setLiveStream(!liveStream)} data-testid="livestream-toggle"
                  className={`w-12 h-7 rounded-full relative transition-colors ${liveStream ? 'bg-petfolk-purple' : ''}`}
                  style={{ background: !liveStream ? 'var(--border-color)' : undefined }}>
                  <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${liveStream ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => setStep(hasCatalog ? 3 : 2)} className="btn-outline" data-testid="back-to-prev">Back</button>
              <button onClick={() => setStep(reviewStep)} className="btn-primary ml-auto" data-testid="next-to-review">
                Review <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Review Step */}
        {step === reviewStep && (
          <div className="animate-fade-in" data-testid="step-review">
            <h2 className="font-outfit text-xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>Review Booking</h2>
            <div className="petfolk-card p-6 space-y-4 mb-6">
              <div className="flex justify-between items-center pb-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
                <div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Pet</p>
                  <p className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>{pet?.name} ({pet?.species})</p>
                </div>
                <span className="text-2xl">{pet?.species === 'DOG' ? '🐕' : '🐈'}</span>
              </div>

              {hasCatalog && selectedPkg && (
                <div className="flex justify-between pb-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <div>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Package</p>
                    <p className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>{selectedPkg.name}</p>
                    {selectedSize && <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Size: {selectedSize === 'EXTRA_LARGE' ? 'Extra Large' : selectedSize}</p>}
                  </div>
                  <p className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>{formatPrice(pkgPrice)}</p>
                </div>
              )}

              {!hasCatalog && service && (
                <div className="flex justify-between pb-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <div>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Service</p>
                    <p className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>{service.name}</p>
                  </div>
                  <p className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>₹{legacyPrice.toLocaleString()}</p>
                </div>
              )}

              {/* A-la-carte items */}
              {hasCatalog && getSelectedAlaCarteDetails().length > 0 && (
                <div className="pb-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>Add-ons</p>
                  {getSelectedAlaCarteDetails().map((item, i) => (
                    <div key={i} className="flex justify-between text-sm py-1">
                      <span style={{ color: 'var(--text-primary)' }}>{item.name}</span>
                      <span className="font-semibold" style={{ color: '#7c3aed' }}>+₹{item.price.toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-between pb-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
                <div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Store</p>
                  <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{node?.displayName}</p>
                </div>
              </div>
              {selectedDate && selectedTime && (
                <div className="flex justify-between pb-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <div>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Schedule</p>
                    <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {new Date(selectedDate).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })} at {selectedTime}
                    </p>
                  </div>
                </div>
              )}
              {groomer && (
                <div className="flex justify-between pb-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <div>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Groomer</p>
                    <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{groomer.name}</p>
                  </div>
                </div>
              )}
              {liveStream && (
                <div className="flex justify-between pb-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Live Streaming</p>
                  <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>+₹149</p>
                </div>
              )}
            </div>

            {/* Coupon */}
            <div className="petfolk-card p-4 mb-4">
              <div className="flex gap-2">
                <input className="petfolk-input flex-1" placeholder="Coupon code" value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())} data-testid="coupon-input" />
                <button onClick={handleApplyCoupon} className="btn-outline text-sm px-4" data-testid="apply-coupon-btn">Apply</button>
              </div>
              {couponDiscount > 0 && <p className="text-sm text-green-600 mt-2">Coupon applied! -₹{couponDiscount}</p>}
            </div>

            {/* Credits */}
            {creditsAvailable > 0 && (
              <div className="petfolk-card p-4 mb-4 flex items-center justify-between" data-testid="credits-section">
                <div>
                  <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Use Credits (₹{creditsAvailable.toFixed(0)} available)</p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Save up to ₹{maxCredits.toFixed(0)}</p>
                </div>
                <button onClick={() => setUseCredits(!useCredits)} data-testid="credits-toggle"
                  className={`w-12 h-7 rounded-full relative transition-colors ${useCredits ? 'bg-green-500' : ''}`}
                  style={{ background: !useCredits ? 'var(--border-color)' : undefined }}>
                  <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${useCredits ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
            )}

            {/* Payment Method */}
            <div className="petfolk-card p-4 mb-6">
              <p className="font-semibold text-sm mb-3" style={{ color: 'var(--text-primary)' }}>Payment Method</p>
              <div className="flex gap-2">
                {[{ v: 'ONLINE', l: 'Pay Online' }, { v: 'CASH', l: 'Pay After Service' }].map(m => (
                  <button key={m.v} onClick={() => setPaymentMethod(m.v)} data-testid={`payment-${m.v}`}
                    className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all ${paymentMethod === m.v ? 'bg-petfolk-orange text-white' : ''}`}
                    style={{ border: `1px solid ${paymentMethod === m.v ? 'transparent' : 'var(--border-color)'}`, color: paymentMethod !== m.v ? 'var(--text-primary)' : undefined }}>
                    {m.l}
                  </button>
                ))}
              </div>
            </div>

            {/* Total */}
            <div className="petfolk-card p-6 mb-6" style={{ background: 'linear-gradient(135deg, rgba(255,140,66,0.08), rgba(139,92,246,0.08))' }}>
              <div className="space-y-2 text-sm mb-3">
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-secondary)' }}>{hasCatalog ? 'Package' : 'Service'}</span>
                  <span style={{ color: 'var(--text-primary)' }}>{formatPrice(basePrice)}</span>
                </div>
                {hasCatalog && alaCarteTotal > 0 && (
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-secondary)' }}>Add-ons ({getSelectedAlaCarteDetails().length})</span>
                    <span style={{ color: 'var(--text-primary)' }}>₹{alaCarteTotal.toLocaleString('en-IN')}</span>
                  </div>
                )}
                {streamFee > 0 && <div className="flex justify-between"><span style={{ color: 'var(--text-secondary)' }}>Live Stream</span><span style={{ color: 'var(--text-primary)' }}>₹{streamFee}</span></div>}
                {couponDiscount > 0 && <div className="flex justify-between text-green-600"><span>Coupon Discount</span><span>-₹{couponDiscount}</span></div>}
                {creditsToUse > 0 && <div className="flex justify-between text-green-600"><span>Credits Used</span><span>-₹{creditsToUse.toFixed(0)}</span></div>}
              </div>
              <div className="flex justify-between items-center pt-3 font-outfit font-bold text-xl" style={{ borderTop: '2px solid var(--border-color)' }}>
                <span style={{ color: 'var(--text-primary)' }}>Total</span>
                <span className="gradient-text">₹{netAmount.toLocaleString('en-IN')}</span>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep(scheduleStep)} className="btn-outline" data-testid="back-to-schedule">Back</button>
              <button onClick={handleSubmit} disabled={submitting} className="btn-primary flex-1 justify-center text-lg py-4" data-testid="confirm-booking-btn">
                {submitting ? <Loader2 className="animate-spin" /> : paymentMethod === 'ONLINE' ? <>Pay ₹{netAmount.toLocaleString('en-IN')} <CreditCard size={18} /></> : <>Confirm Booking <Check size={18} /></>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
